import type { Notification, User } from '@gmn-portal/backend';
import { Archive, Bell, Eye, Loader2, MailCheck } from 'lucide-react';
import { DateTime } from 'luxon';
import { Link, useLocation } from 'react-router-dom';
import { useInfiniteNotifications, useUpdateNotificationStatus } from '@/core/api/notifications';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PageContainer, PageTitle } from '@/shared/components/layout';
import { TabNav } from '@/shared/components/layout/tab-nav';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';

const NOTIFICATIONS_PER_PAGE = 10;

type NotificationContentType = {
  title: string;
  message: string;
};

const EmptyCard = ({ message }: { message: string }) => (
  <Card>
    <CardContent className="flex min-h-[112px] items-center justify-center">
      <p className="text-sm text-muted-foreground">{message}</p>
    </CardContent>
  </Card>
);

const LoadMoreButton = ({
  isFetchingNextPage,
  onClick,
}: {
  isFetchingNextPage: boolean;
  onClick: () => void;
}) => {
  const { t } = useIntl();

  return (
    <div className="flex justify-center">
      <Button
        variant="outline"
        onClick={onClick}
        disabled={isFetchingNextPage}
        className="min-w-[200px]"
      >
        {isFetchingNextPage ? (
          <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            {t('activity.loadingMore')}
          </>
        ) : (
          t('activity.loadMore')
        )}
      </Button>
    </div>
  );
};

const getNotificationContent = (type: string): NotificationContentType => ({
  title: type,
  message: type,
});

const useNotificationLink = (_notification: Notification, user: User | null) => {
  if (!user) return '';
  return user.role === 'gmn-admin'
    ? '/gmn/contract-management/contracts'
    : '/c/my-customer/customer';
};

const NotificationCard = ({
  notification,
  content,
  onUpdateStatus,
  showArchiveButton,
}: {
  notification: Notification;
  content: NotificationContentType;
  onUpdateStatus: (status: 'read' | 'archived') => void;
  showArchiveButton: boolean;
}) => {
  const { t } = useIntl();
  const { locale } = useIntl();
  const { user } = useAuth();
  const isUnread = notification.status === 'unread';
  const link = useNotificationLink(notification, user);

  return (
    <Card>
      <CardHeader className="border-b-0">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">{content.title}</CardTitle>
            </div>
            <CardDescription className="mt-1.5">{content.message}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {isUnread && (
              <Button variant="ghost" size="icon" onClick={() => onUpdateStatus('read')}>
                <MailCheck className="size-4" />
                <span className="sr-only">{t('notifications.actions.markAsRead')}</span>
              </Button>
            )}
            <Button variant="ghost" size="icon" asChild>
              <Link to={link}>
                <Eye className="size-4" />
                <span className="sr-only">{t('notifications.actions.view')}</span>
              </Link>
            </Button>
            {showArchiveButton && (
              <Button variant="ghost" size="icon" onClick={() => onUpdateStatus('archived')}>
                <Archive className="size-4" />
                <span className="sr-only">{t('notifications.actions.archive')}</span>
              </Button>
            )}
          </div>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          {DateTime.fromISO(notification.createdAt).toLocaleString(DateTime.DATETIME_MED, {
            locale,
          })}
        </div>
      </CardHeader>
    </Card>
  );
};

export const Notifications = () => {
  const { t } = useIntl();
  const location = useLocation();
  const currentTab = location.pathname.split('/').pop() || 'unread';

  const {
    data: notifications,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteNotifications({
    sort: '-createdAt',
    where: {
      status: {
        equals: currentTab,
      },
    },
    limit: NOTIFICATIONS_PER_PAGE,
  });

  const { mutate: updateStatus } = useUpdateNotificationStatus();

  const tabs = [
    {
      value: 'unread',
      label: t('notifications.tabs.unread'),
    },
    {
      value: 'read',
      label: t('notifications.tabs.read'),
    },
    {
      value: 'archived',
      label: t('notifications.tabs.archived'),
    },
  ];

  const allNotifications = notifications?.pages.flatMap((page) => page.docs) ?? [];

  return (
    <PageContainer>
      <PageTitle icon={Bell} title={t('common.notifications')} />

      <TabNav tabs={tabs} basePath={location.pathname.replace(/\/(unread|read|archived)?$/, '')} />

      <div className="mt-6 space-y-4">
        {!notifications ? (
          <EmptyCard message={t('common.loading')} />
        ) : allNotifications.length === 0 ? (
          <EmptyCard message={t('notifications.empty')} />
        ) : (
          <>
            {allNotifications.map((notification) => {
              const content = getNotificationContent(notification.type);

              return (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  content={content}
                  onUpdateStatus={(status) => updateStatus({ id: notification.id, status })}
                  showArchiveButton={currentTab !== 'archived'}
                />
              );
            })}

            {hasNextPage && (
              <LoadMoreButton
                isFetchingNextPage={isFetchingNextPage}
                onClick={() => fetchNextPage()}
              />
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
};
